import * as React from 'react';

import {Assistant} from 'nutshell-core/settings-constants';
import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';

import {LeadIconChevron} from 'shells/icon/svg/lead-icon-chevron';
import {TripleChevronIcon} from 'shells/icon/svg/triple-chevron-icon';

import type {
    GetTeamAiIdentity as UserIdentity,
    AiAssistantConversation,
    AiAssistantMessage,
} from 'nutshell-graphql-types';

import {analyticsClient} from '../../analytics-client';
import {nutApolloClient} from '../../apollo-client';

import GET_SEARCH_RESULTS_BY_TYPE from '../navigation/navbar/graphql/queries/get-search-results-by-type.graphql';
import GET_MCFX_VIQ_CONTACTS from '../people-iq/graphql/queries/get-mcfx-viq-contacts.graphql';

import GET_SALES_PROCESS_CONTEXT from './graphql/queries/get-sales-process-context.graphql';
import type {
    LeadSummaryData,
    FindPotentialContactsData,
    InteractionData,
} from './interaction-modal/types';
import type {PageContext} from './redux/consts';

export const SALES_PROCESS_AGENT = 'SALES_PROCESS';
export const SALES_OBJECTION_AGENT = 'SALES_OBJECTION';
export const EMAIL_SEQUENCE_AGENT = 'EMAIL_SEQUENCE';
export const LEAD_RESEARCH_AGENT = 'LEAD_RESEARCH';

export const ALL_AGENT_KEYS = [
    EMAIL_SEQUENCE_AGENT,
    LEAD_RESEARCH_AGENT,
    SALES_OBJECTION_AGENT,
    SALES_PROCESS_AGENT,
] as const;

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Types
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export type Chatbot = {
    updateContext: (newContext: any) => void;
    getContext: () => any;
    getConversation: () => Promise<{
        assistantId: string;
        messages: Message[];
    }>;
    resetConversation: () => Promise<void>;
    registerTool: (tool: Tool) => void;
    unregisterTool: (toolName: string) => void;
    getRegisteredTools: () => Tool[];
    uploadBlob: (myFile: Blob) => Promise<{
        fileId: string;
        objectName: string;
        type: string;
        url: string;
    }>;
    identify: (user: {
        user_id: string;
        user_hash: string;
        user_metadata: {
            firstName?: string | null | undefined;
            lastName?: string | null | undefined;
            // Arbitrary additional metadata can be included
        };
    }) => void;
    ready: () => Promise<void>;
    sendMessage: (message: string) => void;

    // TeamAI will likely add more methods to this type in the future; you can
    // find documentation here: https://docs.teamai.com/sdks/embed-sdk
};

type Message = {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    // If it's a string, it's in ISO 8601 format
    // If it's a number, it's the Unix timestamp in milliseconds
    timestamp: Date | string | number;
};

type Tool = {
    name: string;
    description: string;
    parameters: any;
    execute: (params: any) => any;
};

export type AgentKey = 'SALES_PROCESS' | 'EMAIL_SEQUENCE' | 'SALES_OBJECTION' | 'LEAD_RESEARCH';

export type ToolParameterType = 'string' | 'number' | 'boolean' | 'enum';

export type ToolParameter = {
    name: string;
    type: ToolParameterType;
    description: string;
    required: boolean;
    enumValues?: Array<string>;
};

export type ToolResponse = {
    success: boolean;
    response?: any;
    message?: string;
};

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Agent helpers
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export function getAgentName(agentKey?: AgentKey | null): string {
    if (!agentKey) {
        return 'AI agents';
    }

    switch (agentKey) {
        case SALES_PROCESS_AGENT:
            return 'Sales pipeline designer';
        case EMAIL_SEQUENCE_AGENT:
            return 'Email sequence writer';
        case SALES_OBJECTION_AGENT:
            return 'Sales objection coach';
        case LEAD_RESEARCH_AGENT:
            return 'Lead research agent';
        default:
            return 'AI agent';
    }
}

export function getSubHeaderIcon(entityId?: string | null): React.ReactElement | null {
    if (!entityId) {
        return null;
    }

    switch (true) {
        case entityId.includes('stagesets'):
            return <TripleChevronIcon size={12} />;
        case entityId.includes('leads'):
            return <LeadIconChevron size={12} />;
        default:
            return null;
    }
}

// Dev: https://nutshell-crm.teamai.com/agents
// Prod: https://nutshell-org-default.teamai.com/agents
export function getAssistantIdByAgentKey(agentKey: AgentKey): string {
    if (NutClientConfig.isDevelopment) {
        switch (agentKey) {
            case 'SALES_PROCESS':
                return Assistant.SETTING_ASSISTANT_ID_DEV_SALES_PROCESS_AGENT;
            case 'EMAIL_SEQUENCE':
                return Assistant.SETTING_ASSISTANT_ID_DEV_EMAIL_SEQUENCE_AGENT;
            case 'SALES_OBJECTION':
                return Assistant.SETTING_ASSISTANT_ID_DEV_SALES_OBJECTION_AGENT;
            case 'LEAD_RESEARCH':
                return Assistant.SETTING_ASSISTANT_ID_DEV_LEAD_RESEARCH_AGENT;
            default:
                exhaustivenessCheck(agentKey);
        }
    }

    switch (agentKey) {
        case 'SALES_PROCESS':
            return Assistant.SETTING_ASSISTANT_ID_PROD_SALES_PROCESS_AGENT;
        case 'EMAIL_SEQUENCE':
            return Assistant.SETTING_ASSISTANT_ID_PROD_EMAIL_SEQUENCE_AGENT;
        case 'SALES_OBJECTION':
            return Assistant.SETTING_ASSISTANT_ID_PROD_SALES_OBJECTION_AGENT;
        case 'LEAD_RESEARCH':
            return Assistant.SETTING_ASSISTANT_ID_PROD_LEAD_RESEARCH_AGENT;
        default:
            exhaustivenessCheck(agentKey);
    }
}

export function identifyUser(chatbot: Chatbot, userIdentity: UserIdentity) {
    const {
        teamAiIdentity,
        loggedInUser: {firstName, lastName},
    } = userIdentity;

    chatbot.identify({
        user_id: teamAiIdentity.userId,
        user_hash: teamAiIdentity.userHash,
        user_metadata: {
            firstName,
            lastName,
        },
    });
}

export function updateBaseContext(chatbot: Chatbot, baseContext: string) {
    chatbot.updateContext({baseContext});
}

export function updateSalesProcessContext(chatbot: Chatbot, salesContext: string) {
    chatbot.updateContext({
        agentContext: salesContext,
    });

    registerSalesProcessTools(chatbot);
}

export function updateEmailTemplateContext(chatbot: Chatbot, sequenceContext: string) {
    chatbot.updateContext({
        agentContext: sequenceContext,
    });
}

export function updateSalesObjectionContext(chatbot: any, objectionContext: string) {
    chatbot.updateContext({
        agentContext: objectionContext,
    });
}

export function updateLeadResearchContext(chatbot: Chatbot, pageContext?: string | null) {
    chatbot.updateContext({
        pageContext: pageContext,
    });
}

/**
 * Generates an XML-like string representation of the input that LLMs like
 * Claude are good at parsing. Automatically flattens GraphQL artifacts (such as
 * edges and nodes) to avoid confusion.
 *
 * https://docs.anthropic.com/en/docs/build-with-claude/prompt-engineering/use-xml-tags
 *
 * Only supports types that exist in JSON (strings, numbers, objects, arrays,
 * booleans, and null).
 */
export function generateContextString(context: any): string | null | undefined {
    // Since this function can accept any type, it's easy to accidentally pass
    // in a value that hasn't loaded yet (which will be undefined, or an empty
    // object if it was processed by makeDataSafe). We only want to generate a
    // context string for the real context once it's loaded.
    if (!context || (typeof context === 'object' && Object.keys(context).length === 0)) {
        return null;
    }

    return objectToXmlLikeString(flattenGraphQlArtifacts(context));
}

function flattenGraphQlArtifacts(context: any): any {
    if (typeof context === 'object' && context !== null && !Array.isArray(context)) {
        const cleanContext: Record<string, any> = {};

        const keys = Object.keys(context);
        if (keys.length === 1 && keys[0] === 'edges') {
            const edges = context.edges;
            if (Array.isArray(edges)) {
                const allNodes = edges.every((edge) => {
                    const edgeKeys = Object.keys(edge);

                    return edgeKeys.length === 1 && edgeKeys[0] === 'node';
                });

                if (allNodes) {
                    return edges.map((edge) => edge.node).map(flattenGraphQlArtifacts);
                }
            }
        }

        Object.entries(context).forEach(([key, value]: [any, any]) => {
            cleanContext[key] = flattenGraphQlArtifacts(value);
        });

        return cleanContext;
    }

    if (Array.isArray(context)) {
        return context.map(flattenGraphQlArtifacts);
    }

    return context;
}

function objectToXmlLikeString(context: any): string {
    if (typeof context === 'string') {
        return context;
    }

    if (typeof context === 'number') {
        return context.toString();
    }

    if (typeof context === 'object' && context !== null && !Array.isArray(context)) {
        let contextString = '';

        Object.entries(context).forEach(([key, value]: [any, any]) => {
            const valueString = objectToXmlLikeString(value);
            const extraLineBreak = valueString.includes('\n') ? '\n' : '';
            contextString += `<${key}>${extraLineBreak}${valueString}</${key}>\n`;
        });

        return contextString;
    }

    if (Array.isArray(context)) {
        let contextString = '';

        context.forEach((item) => {
            const itemString = objectToXmlLikeString(item);
            const extraLineBreak = itemString.includes('\n') ? '\n' : '';
            contextString += `<item>${extraLineBreak}${itemString}</item>\n`;
        });

        return contextString;
    }

    if (typeof context === 'boolean') {
        return context.toString();
    }

    if (context === null) {
        return 'null';
    }

    throw new Error(`Unsupported context type: ${typeof context}`);
}

/**
 * A simple heuristic to check if the agent context is loaded. If the entityId
 * is not null, we can assume that the agent context is loaded.
 */
export function isAgentContextLoaded(agentContext: any): boolean {
    return agentContext && agentContext.entityId !== null;
}

export function resyncAiAgentSidebarClasses(isOpen: boolean) {
    const agentMount = document.getElementById('team-ai-sidebar');
    const appMount = document.getElementById('app-react-mount');
    const navbar = document.getElementById('navbar');

    if (agentMount) {
        agentMount.setAttribute('data-is-open', isOpen.toString());
    }
    if (appMount) {
        appMount.setAttribute('data-ai-agent-open', isOpen.toString());
    }
    if (navbar) {
        navbar.setAttribute('data-ai-agent-open', isOpen.toString());
    }
}

export function initializeChatbotWithBaseContext(
    agentKey: AgentKey,
    teamAiIdentity: UserIdentity,
    baseContext: string
): Chatbot | null | undefined {
    const assistantId = getAssistantIdByAgentKey(agentKey);

    // @ts-expect-error
    const chatbot = window.tai.getInstance(assistantId as string);

    if (chatbot) {
        identifyUser(chatbot, teamAiIdentity);
        updateBaseContext(chatbot, baseContext);
        registerBaseTools(chatbot);
    }

    return chatbot;
}

export async function startConversationTracker(
    chatbot: Chatbot,
    keepReportingUsage: {
        current: boolean;
    },
    trackAiAssistantConversation: (input: AiAssistantConversation) => Promise<any>
) {
    while (keepReportingUsage.current) {
        const sleep = new Promise((resolve) => setTimeout(resolve, 5000));

        const conversation = await chatbot.getConversation();

        try {
            // Only attempt to record the conversation if there's more than one
            // message, meaning the user has made some form of interaction with
            // the agent and saving us from accidental double tracking
            if (conversation.messages.length > 1) {
                const input = {
                    assistantId: conversation.assistantId,
                    messages: conversation.messages.map(mapMessage),
                } as const;

                await trackAiAssistantConversation(input);
            }
        } catch (_error: any) {
            // window.trackJs.console.error('Error tracking AI assistant conversation', error);
        }

        await sleep;
    }
}

/**
 * Convert a message from the TeamAI type to our GraphQL type
 */
function mapMessage(message: Message): AiAssistantMessage {
    let role;
    if (message.role === 'user') {
        role = 'USER';
    } else if (message.role === 'assistant') {
        role = 'ASSISTANT';
    } else {
        exhaustivenessCheck(message.role);
    }

    // There's currently a bug in the TeamAI SDK where the timestamp is
    // sometimes a Date object, sometimes an ISO 8601 string, and sometimes a
    // Unix timestamp. Once this is fixed, we can remove this check.
    let timestamp;
    if (message.timestamp instanceof Date) {
        timestamp = message.timestamp;
    } else if (typeof message.timestamp === 'string') {
        timestamp = new Date(message.timestamp);
    } else if (typeof message.timestamp === 'number') {
        timestamp = new Date(message.timestamp);
    } else {
        exhaustivenessCheck(message.timestamp);
    }

    return {
        externalId: message.id,
        // @ts-expect-error
        role,
        content: message.content,
        timestamp: timestamp.toISOString(),
    };
}

export function getShouldDisplayAgentWarning(
    activeAgent?: AgentKey | null,
    pageContextType?: PageContext | null
): boolean {
    if (!activeAgent) {
        return false;
    }

    switch (activeAgent) {
        case LEAD_RESEARCH_AGENT:
            return pageContextType !== 'lead';
        case SALES_PROCESS_AGENT:
            return pageContextType !== 'pipeline';
        // Agents with no preferred page context type
        case EMAIL_SEQUENCE_AGENT:
        case SALES_OBJECTION_AGENT:
        default:
            return false;
    }
}

export function getWarningTextForAgent(agentKey?: AgentKey | null): string | null | undefined {
    if (!agentKey) {
        return null;
    }

    switch (agentKey) {
        case LEAD_RESEARCH_AGENT:
            return 'For the best results, chat with this assistant while viewing a lead.';
        case SALES_PROCESS_AGENT:
            return 'For the best results, chat with this assistant while viewing a pipeline.';
        default:
            return null;
    }
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Tool helpers
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

export function registerTool(
    chatbot: Chatbot,
    name: string,
    description: string,
    callback: (params: any) => Promise<ToolResponse> | ToolResponse,
    parameters?: ToolParameter[]
) {
    chatbot.registerTool({
        name,
        description,
        parameters: {
            type: 'object',
            properties: parameters
                ? parameters.reduce<Record<string, any>>((acc, param) => {
                      acc[param.name] = {
                          type: param.type,
                          description: param.description,
                          ...(param.enumValues ? {enum: param.enumValues} : {}),
                      };

                      return acc;
                  }, {})
                : {},
            required: parameters ? parameters.filter((p) => p.required).map((p) => p.name) : [],
        },
        execute: async (params) => {
            try {
                const result = await callback(params || {});

                return result;
            } catch (error: any) {
                return {
                    success: false,
                    message: error.message,
                };
            }
        },
    });
}

export function unregisterTool(chatbot: Chatbot, name: string) {
    chatbot.unregisterTool(name);
}

export function registerBaseTools(chatbot: Chatbot) {
    registerTool(
        chatbot,
        'search',
        "Search the user's Nutshell account. This tool can only search for companies (accounts), people (contacts), and leads",
        async (params) => {
            const response = await nutApolloClient.query({
                query: GET_SEARCH_RESULTS_BY_TYPE,
                variables: {
                    query: params.searchQuery,
                },
            });

            return {
                success: true,
                response: response.data,
            };
        },
        [
            {
                name: 'searchQuery',
                type: 'string',
                description: 'The search term to look for in Nutshell',
                required: true,
            },
        ]
    );
}

export function unregisterAllPageTools(chatbot: Chatbot) {
    // Pipeline page tools
    unregisterTool(chatbot, PIPELINE_PAGE_TOOL_NAVIGATE);

    // Lead page tools
    unregisterTool(chatbot, LEAD_PAGE_TOOL_POTENTIAL_CONTACTS);
    unregisterTool(chatbot, LEAD_PAGE_TOOL_CREATE_LEAD_SUMMARY_NOTE);
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Agent-specific tools
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const SALES_PROCESS_TOOL_GET_SALES_PROCESS_CONTEXT = 'getSalesProcessContext';
const PIPELINE_PAGE_TOOL_GET_PIPELINE_REPORT = 'getPipelineReport';
export function registerSalesProcessTools(chatbot: Chatbot) {
    registerTool(
        chatbot,
        SALES_PROCESS_TOOL_GET_SALES_PROCESS_CONTEXT,
        "Get information about this Nutshell account's current sales process. This will include pipelines, stages, and products.",
        async () => {
            const response = await nutApolloClient.query({
                query: GET_SALES_PROCESS_CONTEXT,
            });

            return {
                success: true,
                response: response.data,
            };
        }
    );

    registerTool(
        chatbot,
        PIPELINE_PAGE_TOOL_GET_PIPELINE_REPORT,
        'Get an aggregate report about all the leads in a specific pipeline.',
        async (params) => {
            const stagesetId = params.pipelineId;
            if (stagesetId) {
                const url = `/rest/stagesets/${stagesetId}/export`;
                const ajaxParams = {
                    url,
                    dataType: 'text',
                } as const;

                try {
                    const response = await $.ajax(ajaxParams);

                    if (typeof response !== 'string') {
                        return {
                            success: false,
                            message: 'Could not parse the report from Nutshell',
                        };
                    }

                    // Truncate the response to the first 1000 lines to avoid
                    // hitting the token limit. This should still be enough data
                    // to give a valuable report.
                    // https://nutshell.atlassian.net/browse/NUT-26991
                    const truncatedResponse = response.split('\n').slice(0, 1000).join('\n');

                    return {
                        success: true,
                        response: truncatedResponse,
                    };
                } catch (error: any) {
                    return {
                        success: false,
                        message: `An error occurred while getting the report: ${error.message}`,
                    };
                }
            }

            return {
                success: false,
                message: `Pipeline ${params.stagesetId} not found`,
            };
        },
        [
            {
                name: 'pipelineId',
                type: 'number',
                description:
                    'The ID (just an integer, not an API ID) of the pipeline to get the report for',
                required: true,
            },
        ]
    );
}

/* * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
 * Page-specific tools
 * (don't forget to unregister them in unregisterAllPageTools)
 * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * */

const PIPELINE_PAGE_TOOL_NAVIGATE = 'navigatePipelinePage';
export function registerPipelinePageTools(chatbot: Chatbot) {
    registerTool(
        chatbot,
        PIPELINE_PAGE_TOOL_NAVIGATE,
        'Help the user navigate the Nutshell user pipeline interface by clicking a navigation button for them. Do not show the user the names of the button IDs.',
        (params) => {
            const buttonIdMap = {
                USER_ASSIGNMENT: 'user-assignment-button',
                STAGESET_FOLLOW_THROUGH: 'stageset-follow-through-link',
                STAGESET_OPEN_LEADS: 'stageset-open-leads-link',
            } as const;

            // @ts-expect-error
            const buttonId = buttonIdMap[params.buttonType];
            const button = document.getElementById(buttonId);

            analyticsClient.track(analyticsClient.EVENTS.TRACK_AI_ASSISTANT_CLICK_BUTTON_CLICKED);

            if (button) {
                button.click();

                return {success: true};
            }

            return {
                success: false,
                message: `Button ${params.buttonType} not found`,
            };
        },
        [
            {
                name: 'buttonType',
                type: 'string',
                description:
                    'Which button to click. ' +
                    'USER_ASSIGNMENT: This will open a configuration screen where the user can automate who each lead will be assigned to when it enters a stage. ' +
                    'STAGESET_FOLLOW_THROUGH: This will bring the user to a page where they can configure what happens when a lead is closed. What happens can depend on whether the leads is won, lost, or canceled. ' +
                    'STAGESET_OPEN_LEADS: This will bring the user to a page where they can configure the stages for open leads in the pipeline. ',
                required: true,
                enumValues: ['USER_ASSIGNMENT', 'STAGESET_FOLLOW_THROUGH', 'STAGESET_OPEN_LEADS'],
            },
        ]
    );
}

const LEAD_PAGE_TOOL_POTENTIAL_CONTACTS = 'findPotentialContacts';
const LEAD_PAGE_TOOL_CREATE_LEAD_SUMMARY_NOTE = 'createLeadSummaryNote';
export function registerLeadPageTools(
    chatbot: Chatbot,
    onInteractionEvent: (data: InteractionData) => void
) {
    registerTool(
        chatbot,
        LEAD_PAGE_TOOL_POTENTIAL_CONTACTS,
        'Prompts the user to add potential contacts for a given company. ' +
            'This tool is useful for finding potential contacts related to a given company or lead. ' +
            'This tool is useful for lead research and sales prospecting. ' +
            'If you do not find any potential contacts with this tool, ' +
            'attempt to find some using a web search and share them with the user.' +
            'Do not call this tool when making a research report on a lead.',
        async (params) => {
            const companyId = params.companyId;
            if (!companyId) {
                return {
                    success: false,
                    message:
                        'No company ID provided. Do not use this tool if you do not have a company ID.',
                };
            }

            const response = await nutApolloClient.query({
                query: GET_MCFX_VIQ_CONTACTS,
                variables: {
                    id: companyId,
                },
            });

            const potentialContacts = response && response.data && response.data.mcfxViqContacts;

            if (!potentialContacts || potentialContacts.length <= 0) {
                return {
                    success: false,
                    message: 'No potential contacts found for the given company.',
                };
            }

            const fullData: FindPotentialContactsData = {
                __typename: 'FindPotentialContactsData',
                accountId: companyId,
                potentialContacts,
            };

            onInteractionEvent(fullData);

            return {
                success: true,
                message: 'success',
                response: response.data,
            };
        },
        [
            {
                name: 'companyId',
                type: 'string',
                description:
                    'The ID of the company to add potential contacts for. Do not use this tool if you do not have a company API ID.',
                required: true,
            },
        ]
    );

    registerTool(
        chatbot,
        LEAD_PAGE_TOOL_CREATE_LEAD_SUMMARY_NOTE,
        'Suggest a summary note for a given lead.' +
            'Use this tool when doing a research report on a lead to save the report as a note. ' +
            'We should begin each note with a header in this format: ' +
            '✨ Lead research report — {{Lead Name if known. Skip if unknown}} — generated by Nutshell' +
            'Do not include the current date in the header.' +
            'Please use the • symbol only when creating a list. Do not use dashes or other symbols.' +
            'Please add a fun and appropriate emoji at the beginning of each header.',
        async (params) => {
            const suggestedNote = params.suggestedNote;

            const data: LeadSummaryData = {
                __typename: 'LeadSummaryData',
                note: suggestedNote,
                entityId: params.leadId,
            };

            onInteractionEvent(data);

            return {
                success: true,
                message: 'success',
            };
        },
        [
            {
                name: 'suggestedNote',
                type: 'string',
                description:
                    'The suggested note to create for the lead. ' +
                    'This should be a helpful summary of all your research on the lead. ' +
                    'Include any helpful information from your conversation about the lead.',
                required: true,
            },
            {
                name: 'leadId',
                type: 'string',
                description:
                    'The ID of the lead to create a summary note for. ' +
                    'Do not use this tool if you do not have a lead API ID.',
                required: true,
            },
        ]
    );
}
